import { ChangeDetectionStrategy, Component, Injector, Input, OnInit } from '@angular/core';
import { RenderEvent } from '@progress/kendo-angular-charts';
import { Path } from '@progress/kendo-drawing';
import { Rect } from "@progress/kendo-drawing/geometry";
import { take } from 'rxjs/operators';
import { WidgetBaseComponent } from '../../widgetBase';

/**
 * CapitalExpenditureBreakdown Widget
 */
@Component({
  selector: 'capital-expenditure-breakdown-widget',
  templateUrl: './capital-expenditure-breakdown-widget.component.html',
  styleUrls: ['../../common/css/general.scss', './capital-expenditure-breakdown-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CapitalExpenditureBreakdownWidgetComponent extends WidgetBaseComponent implements OnInit {

  /**
   * Widget Guid
   */
  @Input() widgetGuid: string;

  /**
   * Are charts ready to be displayed?
   */
  chartsReady = false;

  /**
   * Chart column data
   */
  public chartDataColumnChart: any[] = [];

  /**
   * Chart DcfCapexBreakdown data
   */
  public chartDataDcfCapexBreakdownEntries: any[] = [];

  /**
   * Chart IncomeCapitalisationCapexBreakdown data
   */
  public chartDataIncomeCapitalisationCapexBreakdownEntries: any[] = [];

  /**
   * Chart DcfCapex Title
   */
  public titleDcfCapex = {
    text: 'Will be translated', font: 'bold 16px Arial'
  };

  /**
   * Chart IncomeCapitalisation Title
   */
  public titleIncomeCapitalisation = {
    text: 'Will be translated', font: 'bold 16px Arial'
  };

  /**
   * Chart Column Title
   */
  public titleColumnChart = {
    text: 'Will be translated', font: 'bold 16px Arial'
  };

  /**
   * Chart TotalCapex Title
   */
  public titleTotalCapex = {
    text: 'Will be translated'
  };

  /**
   * DcfCapex Total
   */
  public dcfCapexTotal = 0;

  /**
   * IncomeCapitalisation Total
   */
  public incomeCapitalisationTotal = 0;

  /**
   * Chart categories
   */
  public categories = [];

  /**
  * Constructor
  */
  constructor(injector: Injector) {
    super(injector, 'capitalExpenditureBreakdown', true);
  }

  /**
   * Initializes the component
   */
  ngOnInit() {
    // will call registerEvents and onInitialized when config (this.widgetConfig and this.page) is ready
    this.initWidgetConfig();
  }

  /**
   * Called by base class to let widget register to various app events (like saving, loading, resizing etc)
   */
  registerEvents(): void {
    this.useReadOnlySaveChangesHandler();
  }

  /**
   * Called by base class to inform widget that it is initialized
   * Mostly used to define where widget data should be loaded from
   */
  onInitialized(): void {
    this.setupLayouts();

    this.mockupData$ = this.widgetService.loadMockup('capitalExpenditureBreakdown').pipe(take(1));

    this.widgetData$ = this.mockup ?
      this.mockupData$
      : this.widgetService.load('capitalExpenditureBreakdown', this.getReport()).pipe();

  }

  /**
   * Called by base class to let widget preprocess loaded data if required
   */
  protected preprocessLoadedData(data) {
    this.reportDesignerTranslator.ready('capitalExpenditureBreakdown').then(() => {
      this.setupCharts(data);
    });

    return data;
  }

  /**
   * Setup widget's charts
   */
  private setupCharts(data: any) {
    this.widgetData = data;
    this.titleColumnChart.text = this.reportDesignerTranslator.getString('capitalExpenditureBreakdown.labelTitleColumnChart');
    this.titleDcfCapex.text = this.reportDesignerTranslator.getString('capitalExpenditureBreakdown.labelTitleDcfCapex');
    this.titleIncomeCapitalisation.text = this.reportDesignerTranslator.getString('capitalExpenditureBreakdown.labelTitleIncomeCapitalisation');
    this.titleTotalCapex.text = this.reportDesignerTranslator.getString('capitalExpenditureBreakdown.labelTotalCapex');

    const labelIncomeCapitalisation = this.reportDesignerTranslator.getString('capitalExpenditureBreakdown.labelIncomeCapitalisation');
    const labelYr1 = this.reportDesignerTranslator.getString('capitalExpenditureBreakdown.labelYear1');
    const labelYr2 = this.reportDesignerTranslator.getString('capitalExpenditureBreakdown.labelYear2');
    const labelYr2To5 = this.reportDesignerTranslator.getString('capitalExpenditureBreakdown.labelYear2To5');
    const labelYr3 = this.reportDesignerTranslator.getString('capitalExpenditureBreakdown.labelYear3');
    const labelYr4 = this.reportDesignerTranslator.getString('capitalExpenditureBreakdown.labelYear4');
    const labelYr5 = this.reportDesignerTranslator.getString('capitalExpenditureBreakdown.labelYear5');
    const labelYr6To10 = this.reportDesignerTranslator.getString('capitalExpenditureBreakdown.labelYear6To10');

    this.chartDataColumnChart = [];
    this.chartDataDcfCapexBreakdownEntries = [];
    this.chartDataIncomeCapitalisationCapexBreakdownEntries = [];

    const useManualData = this.currentLayout === 'Manual Input';

    const labelIncomeCapitalisationCapexTotal = this.reportDesignerTranslator.getString('capitalExpenditureBreakdown.incomeCapitalisationCapexTotal');
    const labelIncomeCapitalisationCapexAsPartOfLettingStrategy = this.reportDesignerTranslator.getString('capitalExpenditureBreakdown.incomeCapitalisationCapexAsPartOfLettingStrategy');
    const labelIncomeCapitalisationCapexForSustainability = this.reportDesignerTranslator.getString('capitalExpenditureBreakdown.incomeCapitalisationCapexForSustainability');

    //Get the income capitalization data (1st column / 2nd donut chart)
    let incomeCapitalisationCapexTotal;
    let incomeCapitalisationCapexAsPartOfLettingStrategy;
    let incomeCapitalisationCapexForSustainability;


    //Get the DCF totals for the 1st donut chart

    let dcfCapitalExpendituresTotal;
    let dcfCapexAsPartOfLettingStrategy;
    let dcfCapexForSustainability;

    this.dcfCapexTotal = this.widgetData.dcfCapexTotal;
    this.incomeCapitalisationTotal = this.widgetData.incomeCapitalisationTotal;

    if (useManualData) {
      this.categories = [
        labelIncomeCapitalisation,
        labelYr1,
        labelYr2To5,
        labelYr6To10
      ];

      incomeCapitalisationCapexTotal = this.widgetData.incomeCapitalisationCapexTotalManual;
      incomeCapitalisationCapexAsPartOfLettingStrategy = this.widgetData.incomeCapitalisationCapexAsPartOfLettingStrategyManual;
      incomeCapitalisationCapexForSustainability = this.widgetData.incomeCapitalisationCapexForSustainabilityManual;

      dcfCapitalExpendituresTotal = this.widgetData.dcfCapex.map(e => e.dcfCapitalExpendituresTotalManual).reduce((prev, curr) => prev + Math.abs(curr), 0);
      dcfCapexAsPartOfLettingStrategy = this.widgetData.dcfCapex.map(e => e.dcfCapexAsPartOfLettingStrategyManual).reduce((prev, curr) => prev + Math.abs(curr), 0);
      dcfCapexForSustainability = this.widgetData.dcfCapex.map(e => e.dcfCapexForSustainabilityManual).reduce((prev, curr) => prev + Math.abs(curr), 0);

      this.dcfCapexTotal = this.widgetData.dcfCapexTotalManual;
      this.incomeCapitalisationTotal = this.widgetData.incomeCapitalisationTotalManual;
    }
    else {
      this.categories = [
        labelIncomeCapitalisation,
        labelYr1,
        labelYr2,
        labelYr3,
        labelYr4,
        labelYr5,
        labelYr6To10
      ];

      incomeCapitalisationCapexTotal = this.widgetData.incomeCapitalisationCapexTotal;
      incomeCapitalisationCapexAsPartOfLettingStrategy = this.widgetData.incomeCapitalisationCapexAsPartOfLettingStrategy;
      incomeCapitalisationCapexForSustainability = this.widgetData.incomeCapitalisationCapexForSustainability;

      dcfCapitalExpendituresTotal = this.widgetData.dcfCapex.map(e => e.dcfCapitalExpendituresTotal).reduce((prev, curr) => prev + Math.abs(curr), 0);
      dcfCapexAsPartOfLettingStrategy = this.widgetData.dcfCapex.map(e => e.dcfCapexAsPartOfLettingStrategy).reduce((prev, curr) => prev + Math.abs(curr), 0);
      dcfCapexForSustainability = this.widgetData.dcfCapex.map(e => e.dcfCapexForSustainability).reduce((prev, curr) => prev + Math.abs(curr), 0);
    }

    this.chartDataColumnChart = [
      {
        name: labelIncomeCapitalisationCapexTotal,
        color: '#000000',
        data: this.getChartData('dcfCapitalExpendituresTotal', useManualData)
      },
      {
        name: labelIncomeCapitalisationCapexAsPartOfLettingStrategy,
        color: '#DBD6C7',
        data: this.getChartData('dcfCapexAsPartOfLettingStrategy', useManualData)
      },
      {
        name: labelIncomeCapitalisationCapexForSustainability,
        color: '#E30613',
        data: this.getChartData('dcfCapexForSustainability', useManualData)
      },
    ];



    //Insert the income capitalization data into the 1st column
    this.chartDataColumnChart[0].data.unshift(incomeCapitalisationCapexTotal);
    this.chartDataColumnChart[1].data.unshift(incomeCapitalisationCapexAsPartOfLettingStrategy);
    this.chartDataColumnChart[2].data.unshift(incomeCapitalisationCapexForSustainability);


    this.chartDataDcfCapexBreakdownEntries.push({ color: '#000000', label: labelIncomeCapitalisationCapexTotal, value: Math.abs(dcfCapitalExpendituresTotal) * 100 / this.dcfCapexTotal });
    /*lettingInitiativeCapex*/
    this.chartDataDcfCapexBreakdownEntries.push({ color: '#DBD6C7', label: labelIncomeCapitalisationCapexAsPartOfLettingStrategy, value: Math.abs(dcfCapexAsPartOfLettingStrategy) * 100 / this.dcfCapexTotal });
    /*sustainabilityCapex*/
    this.chartDataDcfCapexBreakdownEntries.push({ color: '#E30613', label: labelIncomeCapitalisationCapexForSustainability, value: Math.abs(dcfCapexForSustainability) * 100 / this.dcfCapexTotal });


    this.chartDataIncomeCapitalisationCapexBreakdownEntries.push({ color: '#000000', label: labelIncomeCapitalisationCapexTotal, value: Math.abs(incomeCapitalisationCapexTotal) * 100 / this.incomeCapitalisationTotal });
    /*lettingInitiativeCapex*/
    this.chartDataIncomeCapitalisationCapexBreakdownEntries.push({ color: '#DBD6C7', label: labelIncomeCapitalisationCapexAsPartOfLettingStrategy, value: Math.abs(incomeCapitalisationCapexAsPartOfLettingStrategy) * 100 / this.incomeCapitalisationTotal });
    /*sustainabilityCapex*/
    this.chartDataIncomeCapitalisationCapexBreakdownEntries.push({ color: '#E30613', label: labelIncomeCapitalisationCapexForSustainability, value: Math.abs(incomeCapitalisationCapexForSustainability) * 100 / this.incomeCapitalisationTotal });

    this.chartsReady = true;
    this.ref.markForCheck();
  }

  /**
   * Chart onRender event handler
   */
  public onRender = (args: RenderEvent): void => {
    const chart = args.sender;

    const valueAxis = chart.findAxisByName('first');
    const categoryAxis = chart.findAxisByName('categoryAxis');

    const categorySlot = categoryAxis.slot(1, 1) as Rect;
    const valRange = valueAxis.range();
    const valueSlot = valueAxis.slot(valRange.min as number, valRange.max as number) as Rect;

    // draw the plot band based on the found coordinates
    const line = new Path({
      stroke: {
        dashType: "dash",
        color: 'black',
        width: 2
      }
    }).moveTo(categorySlot.origin.x, valueSlot.origin.y)
      .lineTo(categorySlot.origin.x, valueSlot.origin.y + valueSlot.size.height);

    chart.surface.draw(line); // Draw on the Chart surface to overlay the series
    // Or as a first element in the pane to appear behind the series -> chart.findPaneByIndex(0).visual.insert(0, line);
  };

  /**
   * Return chart data
   */
  private getChartData(fieldName: string, useManualData: boolean): any[] {
    let data;
    if (useManualData) {
      fieldName += 'Manual';
      data = this.widgetData.dcfCapex.filter((data) => { return data.year <= 2 });
    }
    else {
      data = this.widgetData.dcfCapex.filter((data) => { return data.year <= 5 });
    }

    const dataYear6Onwards = this.widgetData.dcfCapex.filter((data) => { return data.year >= 6 });
    const sum = dataYear6Onwards.map(e => e[fieldName]).reduce((prev, curr) => prev + Math.abs(curr), 0);

    const ret = data.map((entry) => entry[fieldName]);
    ret.push(sum);

    return ret;
  }

  /**
   * Setup widget's layout if required
   */
  setupLayouts() {
    const fixLayouts = this.availableLayouts.length === 0 && this.currentLayout === 'default';

    if (fixLayouts) {
      this.currentLayout = 'Default';
    }
  }

  /**
   * Layout changed event handler
  * @param newLayout {string} New layout
   */
  public layoutChanged(newLayout: string): void {
    super.layoutChanged(newLayout);

    this.chartsReady = true;
    this.ref.markForCheck();
    setTimeout(() => {
      this.setupCharts(this.widgetData);
    });
  }
}
